// Reload the app when a new frontend build is deployed, so a WebView resumed
// from the background (or a long-open tab) doesn't keep running an old bundle.
//
// The build id is baked in at build time (craco.config.js) and the same value is
// written to /version.json. On focus we fetch /version.json (no-store, cache-busted)
// from the app's own origin — no dependency on the backend.
//
// A reload never interrupts the active workout or onboarding: it is deferred
// until the user next lands on another screen.

export const CLIENT_BUILD = process.env.REACT_APP_BUILD_HASH || '';
const RELOADED_FOR_KEY = 'bm_reloaded_for_build';
const MIN_INTERVAL_MS = 30000;

const known = (build) => !!build && build !== 'unknown';

/** Reload only when both ids are known, they differ, and we haven't already reloaded for this build. */
export function shouldReload(clientBuild, serverBuild, reloadedFor) {
  return known(clientBuild) && known(serverBuild) && clientBuild !== serverBuild && reloadedFor !== serverBuild;
}

/** Screens where a reload would lose the user's place. */
export function isReloadBlocked(pathname) {
  return pathname === '/onboarding' || pathname.startsWith('/onboarding/') || pathname.startsWith('/workout/active/');
}

function readReloadedFor() {
  try { return sessionStorage.getItem(RELOADED_FOR_KEY); } catch { return null; }
}

function markReloadedFor(build) {
  try { sessionStorage.setItem(RELOADED_FOR_KEY, build); } catch {}
}

export async function fetchDeployedBuild(fetchImpl = (...args) => window.fetch(...args)) {
  const res = await fetchImpl(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.build || null;
}

/**
 * check()          — call on focus/visibility; reloads now, or marks a reload pending on blocked screens.
 * onRouteChange()  — call on navigation; performs a pending reload once the new screen isn't blocked.
 */
export function createVersionChecker({
  clientBuild = CLIENT_BUILD,
  fetchBuild = () => fetchDeployedBuild(),
  reload = () => window.location.reload(),
  getPath = () => window.location.pathname,
  now = () => Date.now(),
  minIntervalMs = MIN_INTERVAL_MS,
} = {}) {
  let lastCheck = -Infinity;
  let inFlight = false;
  let pendingBuild = null;

  const reloadFor = (build) => {
    pendingBuild = null;
    // Guard against a reload loop if the served bundle is still the old one.
    markReloadedFor(build);
    console.info(`[Version] build changed ${clientBuild} -> ${build}, reloading`);
    reload();
  };

  const check = async () => {
    if (!known(clientBuild) || inFlight || now() - lastCheck < minIntervalMs) return;
    inFlight = true;
    lastCheck = now();
    try {
      const build = await fetchBuild();
      if (!shouldReload(clientBuild, build, readReloadedFor())) return;
      if (isReloadBlocked(getPath())) {
        pendingBuild = build;
        console.info(`[Version] new build ${build} — reload deferred until the user leaves this screen`);
      } else {
        reloadFor(build);
      }
    } catch {
      // Offline or version.json unavailable: try again on the next focus.
    } finally {
      inFlight = false;
    }
  };

  const onRouteChange = (pathname) => {
    if (pendingBuild && !isReloadBlocked(pathname)) reloadFor(pendingBuild);
  };

  return { check, onRouteChange, hasPendingReload: () => !!pendingBuild };
}

let activeChecker = null;

/** Start listening for focus/visibility. Returns a stop function. */
export function startVersionCheck(options = {}) {
  const checker = createVersionChecker(options);
  if (!known(options.clientBuild ?? CLIENT_BUILD)) return () => {};
  activeChecker = checker;
  const onFocus = () => { if (document.visibilityState !== 'hidden') checker.check(); };
  document.addEventListener('visibilitychange', onFocus);
  window.addEventListener('focus', onFocus);
  return () => {
    document.removeEventListener('visibilitychange', onFocus);
    window.removeEventListener('focus', onFocus);
    if (activeChecker === checker) activeChecker = null;
  };
}

/** Called by the router on every navigation (see VersionReloadOnNavigate in App.js). */
export function notifyRouteChange(pathname) {
  activeChecker?.onRouteChange(pathname);
}
